import type { UseQueryResult } from '@tanstack/react-query';

import { type LiceSource, useLiceSource } from '@/hooks/useLiceSource';
import {
    useLiceController_DailyLice,
    useLiceController_Last200Lice,
    useLiceController_WeeklyLice,
} from '@/services/hooks';
import type {
    LiceController_DailyLiceQueryParams,
    LiceController_Last200LiceQueryParams,
    LiceController_WeeklyLiceQueryParams,
    LiceParityDto,
} from '@/services/types';

import { mapDailyLiceCounts, mapLast3DaysLiceCount, mapWeeklyLiceCounts } from './mappers';
import type {
    DomainDailyLiceCount,
    DomainLast3DaysLiceCount,
    DomainWeeklyLiceCount,
} from './types';

/**
 * The cage lice series.
 *
 * The one place the UI reaches `services/` for lice counts, so the lice pages follow the house
 * rule that components import from `domain/`. What that buys immediately is a single seam: when
 * the endpoints grow a parameter — and the implementation switch behind the calculation rebuild
 * is the next one — it is added here rather than in seven containers.
 *
 * The react-query result is passed through with `data` replaced by the mapped value, so
 * `error` and `refetch` keep working exactly as they did, and `isLoading` keeps meaning "no data
 * yet" (react-query's `isPending`, as in every domain hook).
 *
 * **`data` is the rows.** The generated hooks hand back an axios response, so every consumer
 * today reaches through `query.data.data` and guards each step — `dailyLice?.data?.data?.map(…)`.
 * Unwrapping it once here is most of what makes the containers shorter, and it is why migrating
 * one is not purely a change of import.
 */

/**
 * `source` is deliberately not part of any of these.
 *
 * Which implementation answers is a PostHog flag, read inside each hook rather than passed in.
 * A cage page mounts these from several containers at once and relies on producing an identical
 * react-query key to share one request; reading the flag here means they cannot disagree about
 * it, and no caller can pin a source by hand and quietly diverge from the rollout.
 *
 * `fishGroupId`, on the other hand, is a parameter: the cohort the page names (`fg`), handed in
 * by the containers exactly as they hand it to the overview. With one, the backend answers for the
 * group and always from `core`.
 */
export type CageDailyLiceParams = Omit<LiceController_DailyLiceQueryParams, 'source'>;
export type CageWeeklyLiceParams = Omit<LiceController_WeeklyLiceQueryParams, 'source'>;
export type CageLast200LiceParams = Omit<LiceController_Last200LiceQueryParams, 'source'>;

type DailyOptions = Parameters<typeof useLiceController_DailyLice>[2];
type WeeklyOptions = Parameters<typeof useLiceController_WeeklyLice>[2];
// [2]: the endpoint's query params sit at [1], so react-query's options are one along.
// Reading the slot by position keeps this in step if the generated signature moves again.
type Last200Options = Parameters<typeof useLiceController_Last200Lice>[2];

/**
 * The react-query result with `data` replaced by the mapped rows.
 *
 * `Omit` rather than an interface of its own, so `error`, `refetch` and everything else keep
 * whatever react-query says they are instead of a copy that drifts. `isLoading` is the one
 * override: it is set from `isPending`, the domain-wide meaning.
 */
export type DomainQueryResult<TData> = Omit<UseQueryResult<unknown, unknown>, 'data'> & {
    data: TData;
};

/**
 * A lice series, plus what the response says about how it was produced.
 *
 * The endpoints answer with an object — `{ days, source, parity? }` — rather than a bare array,
 * which is what lets them carry this at all. `data` is still just the rows, so a component sees
 * exactly what it saw before; the provenance is beside it for whoever is driving the rollout.
 */
export type DomainLiceQueryResult<TData> = DomainQueryResult<TData> & {
    /**
     * Which implementation actually answered, after the server applied its own rules. Requested
     * and resolved are not the same thing — a fish group pins it to `core` regardless, and a
     * `compare` whose `core` side failed answers from `sql` and says `sql` — so this is read off
     * the response rather than assumed from the flag.
     */
    source: LiceSource | undefined;
    /**
     * Only for a requested `compare`: how `sql` and `core` differed on this exact request.
     * Present so the comparison can be read from the browser on production traffic, instead of
     * by tailing the API logs or calling the admin-only parity endpoint.
     *
     * A report with `error` set is a comparison that never ran: `core` failed, `sql` served, and
     * `source` is `sql` rather than `compare`.
     */
    parity: LiceParityDto | undefined;
};

/** Daily counts, each carrying the three-day rolling average ending on it. */
export function useGetCageDailyLice(
    cageId: number,
    params: CageDailyLiceParams,
    options?: DailyOptions
): DomainLiceQueryResult<DomainDailyLiceCount[]> {
    const source = useLiceSource();
    const query = useLiceController_DailyLice(cageId, { ...params, source }, options);
    const response = query.data?.data;

    return {
        ...query,
        isLoading: query.isPending,
        data: mapDailyLiceCounts(response?.days),
        source: response?.source,
        parity: response?.parity,
    };
}

/** One point per ISO week that has data. */
export function useGetCageWeeklyLice(
    cageId: number,
    params: CageWeeklyLiceParams,
    options?: WeeklyOptions
): DomainLiceQueryResult<DomainWeeklyLiceCount[]> {
    const source = useLiceSource();
    const query = useLiceController_WeeklyLice(cageId, { ...params, source }, options);
    const response = query.data?.data;

    return {
        ...query,
        isLoading: query.isPending,
        data: mapWeeklyLiceCounts(response?.weeks),
        source: response?.source,
        parity: response?.parity,
    };
}

/** The newest three-day snapshot, or `null` when there is none to publish. */
export function useGetCageLast200Lice(
    cageId: number,
    params: CageLast200LiceParams = {},
    options?: Last200Options
): DomainLiceQueryResult<DomainLast3DaysLiceCount | null> {
    const source = useLiceSource();
    const query = useLiceController_Last200Lice(cageId, { ...params, source }, options);
    const response = query.data?.data;

    return {
        ...query,
        isLoading: query.isPending,
        data: mapLast3DaysLiceCount(response?.snapshot),
        source: response?.source,
        parity: response?.parity,
    };
}
